import {
  LayoutDashboard,
  ClipboardList,
  CreditCard,
  Users,
  Network,
  Store,
  Package,
  CalendarDays,
  Zap,
  MapPinned,
  MapPin,
  ScrollText,
  MessageCircle,
  BarChart2,
  Settings,
  LogOut,
  Grid3X3,
  Layers,
  TrendingUp,
  Tag,
  Globe
} from 'lucide-react';
import { Portal } from '../types';
import { MyStore, getStoreInitials } from '../hooks/useMyStore';

interface SidebarProps {
  portal: Portal;
  currentPage: string;
  onNavigate: (page: string) => void;
  setupComplete: boolean;
  store?: MyStore | null;
}

export const Sidebar: React.FC<SidebarProps> = ({
  portal,
  currentPage,
  onNavigate,
  setupComplete,
  store,
}) => {
  const isMfr = portal === 'manufacturer';
  const storeName = store?.storeName || (isMfr ? 'Manufacturer' : 'Distributor');
  const initials = getStoreInitials(store?.storeName) || (isMfr ? 'M' : 'D');

  const NavItem = ({ id, icon: Icon, label }: { id: string, icon: any, label: string }) => {
    const active = currentPage === id;
    return (
      <button
        onClick={() => onNavigate(id)}
        className={`w-full h-[34px] px-3 flex items-center gap-3 text-[13px] transition-all duration-200 group relative
          ${active
            ? 'bg-primary/15 text-white border-l-[2.5px] border-primary'
            : 'text-white/45 hover:bg-white/5 hover:text-white'}`}
      >
        <Icon size={16} className={active ? 'text-primary' : 'text-white/45 group-hover:text-white'} />
        <span className="font-medium">{label}</span>
      </button>
    );
  };

  const SectionLabel = ({ label }: { label: string }) => (
    <div className="text-[9px] uppercase tracking-wider text-white/25 mt-5 mb-1 ml-3 font-bold">
      {label}
    </div>
  );

  return (
    <aside className="w-[248px] flex-shrink-0 h-screen bg-sidebar flex flex-col overflow-hidden">
      {/* Logo Area */}
      <div className="h-[52px] flex items-center px-4 gap-3 border-b border-white/5">
        <div className="w-8 h-8 rounded-[10px] bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] flex items-center justify-center text-white font-bold text-sm">
          FA
        </div>
        <span className="text-white font-bold text-lg tracking-tight">FieldAssist</span>
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm uppercase
          ${isMfr ? 'bg-amber-500/20 text-amber-500' : 'bg-green-500/20 text-green-500'}`}>
          {isMfr ? 'MFR' : 'DIST'}
        </span>
      </div>

      {/* Company Area */}
      {/* <div className="h-[44px] flex items-center px-4 gap-3 bg-white/5">
        <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-white text-[10px] font-bold">
          {initials}
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-white text-[13px] font-semibold truncate leading-tight" title={storeName}>
            {storeName}
          </span>
          <span className="text-white/30 text-[9px] uppercase font-bold tracking-wider">
            {isMfr ? 'MANUFACTURER' : 'DISTRIBUTOR'}
          </span>
        </div>
      </div> */}

      {/* Nav Area */}
      <nav className="flex-1 overflow-y-auto py-2 custom-scrollbar">
        <SectionLabel label="OVERVIEW" />
        <NavItem id={`${isMfr ? 'mfr' : 'dist'}-dashboard`} icon={LayoutDashboard} label="Dashboard" />
        <NavItem id={`${isMfr ? 'mfr' : 'dist'}-bookings`} icon={ClipboardList} label="Booking Overview" />

        <SectionLabel label="SETUP" />
        <NavItem
          id={setupComplete ? 'setup-manage' : 'setup-wizard'}
          icon={CreditCard}
          label="Subscription"
        />
        <NavItem id={`${isMfr ? 'mfr' : 'dist'}-users`} icon={Users} label="Field Users" />

        <SectionLabel label="ASSIGNMENT" />
        {/* Distributor Assignment — hidden for now; uncomment to re-enable.
        {isMfr && <NavItem id="mfr-assign-distributors" icon={Network} label="Distributor Assignment" />}
        */}
        <NavItem id={`${isMfr ? 'mfr' : 'dist'}-assign-retailers`} icon={Store} label="Retailer Assignment" />
        <NavItem id={`${isMfr ? 'mfr' : 'dist'}-visit-scheduler`} icon={CalendarDays} label="Visit Scheduler" />

        {/* Custom stores manage their own retailer directory (RiEtail stores get this via the connector). */}
        {store?.storeType === 'custom' && (
          <NavItem id="custom-retailers" icon={Store} label="Retailers" />
        )}

        <SectionLabel label="CATALOG" />
        <NavItem id={`${isMfr ? 'mfr' : 'dist'}-catalog`} icon={Package} label={isMfr ? "Product Catalog" : "My Catalog"} />

        {/* <SectionLabel label="OPERATIONS" />
        <NavItem id={`${isMfr ? 'mfr' : 'dist'}-operations`} icon={Zap} label="Operations" /> */}

        {/* <SectionLabel label="NOTIFICATIONS" />
        <NavItem id={`${isMfr ? 'mfr' : 'dist'}-whatsapp`} icon={MessageCircle} label="WhatsApp Â· RI Message" /> */}

        {!isMfr && (
          <>
            <SectionLabel label="MY RETAILERS" />
            <NavItem id="dist-retailer-directory" icon={Store} label="Retailer Directory" />
            <NavItem id="dist-retailer-groups" icon={Grid3X3} label="Retailer Groups" />
          </>
        )}

        <SectionLabel label="REPORTS" />
        <NavItem id={`${isMfr ? 'mfr' : 'dist'}-reports`} icon={BarChart2} label="Reports & Analytics" />

        <SectionLabel label="SETTINGS" />
        <NavItem id={`${isMfr ? 'mfr' : 'dist'}-settings`} icon={Settings} label="Settings" />
      </nav>

      {/* Logout Area — custom stores only. RiEtail stores authenticate
          via an external URL token, so there's no local session to clear
          from inside the app. */}
      {store?.storeType === 'custom' && (
        <div className="h-[44px] border-t border-white/5 shrink-0">
          <button
            onClick={() => onNavigate('login')}
            className="w-full h-full px-4 flex items-center gap-3 text-white/45 hover:text-white hover:bg-white/5 transition-colors"
          >
            <LogOut size={16} />
            <span className="text-[13px] font-medium">Logout</span>
          </button>
        </div>
      )}
    </aside>
  );
};
